<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import {
  isImage,
  isVideo,
} from '@nora-monorepo/ui-shared';

import AttachmentFileCard from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-view/AttachmentFileCard.vue';
import AttachmentGallerySideBar from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-view/AttachmentGallerySideBar.vue';
import { useMediaRetry } from '#nora-chat-ui/composables/useMediaRetry';

interface AttachmentGalleryProps {
  attachment: MessageAttachmentDTO;
}

const props = defineProps<AttachmentGalleryProps>();
const emit = defineEmits<{
  close: [];
  refreshUrls: [];
}>();

const { attachment } = toRefs(props);
const mimeType = attachment.value.mimeType;
const attachmentSource = computed(() => attachment.value.urls?.download?.url);
const downloadUrl = computed(() => `${attachment.value.urls?.download?.url}&contentDisposition=inline`);

const { t } = useI18n();

const videoRef = ref<HTMLVideoElement | null>(null);

const {
  isError,
  handleError: handleMediaError,
  handleSuccess,
} = useMediaRetry({
  urlRef: attachmentSource,
  onRefreshRequest: () => emit('refreshUrls'),
  videoRef,
});

const MAX_IMAGE_RELOAD_ATTEMPTS = 3;
const imageReady = ref(false);
const imageFailed = ref(false);
const imageReloadAttempts = ref(0);

function onImageLoad(): void {
  imageReady.value = true;
  imageFailed.value = false;
  imageReloadAttempts.value = 0;
}

function onImageError(): void {
  imageReady.value = false;
  imageFailed.value = true;

  if (imageReloadAttempts.value < MAX_IMAGE_RELOAD_ATTEMPTS) {
    imageReloadAttempts.value++;

    emit('refreshUrls');
  }
}

function openInNewWindow(): void {
  if (downloadUrl.value) {
    window.open(downloadUrl.value, '_blank');
  }
}

function handleEscapeKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    emit('close');
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKeyDown);
});
</script>

<template>
  <div
    v-if="attachmentSource"
    class="fixed w-full h-full inset-0 bg-[#0A0A0A]/85 flex items-center justify-center z-9997"
    @click="$emit('close')"
  >
    <AttachmentGallerySideBar
      :attachment="attachment"
      @close-gallery-view="() => emit('close')"
    />

    <div
      v-if="isImage(mimeType)"
      class="relative h-full flex items-center justify-center"
    >
      <img
        v-show="imageReady"
        :src="attachmentSource"
        alt=""
        class="max-w-[95%] max-h-[95%] bg-black"
        @click.stop
        @load="onImageLoad"
        @error="onImageError"
      >
      <AttachmentFileCard
        v-show="imageFailed"
        :attachment="attachment"
        @open="openInNewWindow"
      />
    </div>
    <div
      v-else-if="isVideo(mimeType)"
      class="relative flex items-center justify-center h-full"
      @click="emit('close')"
    >
      <AttachmentFileCard
        v-if="isError"
        :attachment="attachment"
        @open="openInNewWindow"
      />
      <video
        v-else
        ref="videoRef"
        controls
        autoplay
        class="max-h-[95%] max-w-[95%]"
        @click.stop
        @loadeddata="handleSuccess"
        @error="handleMediaError"
      >
        <source
          :src="attachmentSource"
          type="video/mp4"
        >
        <source
          :src="attachmentSource"
          type="video/ogg"
        >
        {{ t('error.videoNotSupported') }}
      </video>
    </div>
    <AttachmentFileCard
      v-else
      :attachment="attachment"
      @open="openInNewWindow"
    />
  </div>
</template>
